import { DemandProduct } from '../types/DemandProduct';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { IDemandAddProductDeliveryForm } from '../display/content/add-product/demand-add-product.component';
import { Demand } from '../types/Demand';
import { EprocUser } from '@lib/user';
import { DisplayCurrency } from '@lib/util';
import dayjs from 'dayjs';

export interface IDemandAddProductDataForm {
    name: FormControl<string>;
    description: FormControl<string>;
    customerProductNumber: FormControl<string>;
    customerProductNumberIsKnown: FormControl<boolean>;
    manufacturer: FormControl<string>;
    manufacturerArticleNumber: FormControl<string>;
    supplierArticleNumber: FormControl<string>;
    numberOfContentUnitsPerOrderUnit: FormControl<number>;
    contentUnit: FormControl<string>;
    requestUnit: FormControl<string>;
    requestQuantity: FormControl<number>;
}

export interface IDemandAddProductPriceForm {
    unitPrice: FormControl<number>;
    currency: FormControl<DisplayCurrency>;
    taxPercent: FormControl<number>;
}

function trim(str: string): string {
    if (typeof str !== 'undefined' && str !== null) {
        str = str.trim();
    }

    return str;
}

export class CreateDemandProductService {
    public initDataFormGroup(
        formBuilder: UntypedFormBuilder,
        product: DemandProduct,
        demand: Demand,
        supplierArticleNumberRequired: boolean,
        defaultArticleNumber?: string,
    ): FormGroup<IDemandAddProductDataForm> {
        const customerProductNumberIsKnownControl = new FormControl<boolean>(
            product.data.customerProductNumber ? defaultArticleNumber !== product.data.customerProductNumber : false,
        );
        const formGroup: FormGroup<IDemandAddProductDataForm> = formBuilder.group({
            name: [product.data.name, [Validators.required, Validators.maxLength(150)]],
            description: [product.data.description, Validators.maxLength(10000)],
            customerProductNumber: [product.data.customerProductNumber, Validators.maxLength(50)],
            customerProductNumberIsKnown: customerProductNumberIsKnownControl,
            manufacturer: [product.data.manufacturer, Validators.maxLength(50)],
            manufacturerArticleNumber: [product.data.manufacturerArticleNumber, Validators.maxLength(50)],
            supplierArticleNumber: [product.data.supplierArticleNumber, Validators.maxLength(50)],
            numberOfContentUnitsPerOrderUnit: [product.data.numberOfContentUnitsPerOrderUnit, Validators.required],
            contentUnit: [product.data.contentUnit, Validators.required],
            requestUnit: [product.orderUnit, Validators.required],
            requestQuantity: [product.quantity, Validators.required],
        });

        if (supplierArticleNumberRequired) {
            formGroup.controls['supplierArticleNumber'].addValidators(Validators.required);
        }

        return formGroup;
    }

    public initPriceFormGroup(
        formBuilder: UntypedFormBuilder,
        product: DemandProduct,
        user: EprocUser,
    ): FormGroup<IDemandAddProductPriceForm> {
        return formBuilder.group({
            unitPrice: new FormControl(product.price.unitPrice, { nonNullable: true }),
            currency: new FormControl<DisplayCurrency>(
                new DisplayCurrency(product.price.currency, user.language.iso639LanguageAlpha2),
                { nonNullable: true, validators: [Validators.required] },
            ),
            taxPercent: [product.price.taxRate],
        });
    }

    public initDeliveryFormGroup(
        formBuilder: UntypedFormBuilder,
        product: DemandProduct,
        deliveryDateRequired: boolean,
    ): FormGroup<IDemandAddProductDeliveryForm> {
        const form: FormGroup<IDemandAddProductDeliveryForm> = formBuilder.group({
            deliveryTime: [product.delivery.deliveryTime],
            deliveryTimeUnit: [product.delivery.deliveryTimeUnit],
            preferredDeliveryDate: [
                !product.delivery.preferredDeliveryDate ? undefined : dayjs(product.delivery.preferredDeliveryDate),
            ],
            partialDeliveryPossible: [product.delivery.partialDeliveryPossible],
        });

        if (deliveryDateRequired) {
            form.controls.preferredDeliveryDate.addValidators(Validators.required);
        }

        return form;
    }

    public insertDataIntoProduct(
        product: DemandProduct,
        dataGroup: FormGroup<IDemandAddProductDataForm>,
        priceGroup: FormGroup<IDemandAddProductPriceForm>,
        deliveryGroup: FormGroup<IDemandAddProductDeliveryForm>,
        comment: string,
        commentInternal: string,
    ): void {
        product.data.name = trim(dataGroup.value.name!);
        product.data.description = trim(dataGroup.value.description!);
        product.data.customerProductNumber = trim(
            dataGroup.value.customerProductNumberIsKnown && dataGroup.value.customerProductNumber
                ? dataGroup.value.customerProductNumber
                : '',
        );
        product.data.manufacturer = trim(dataGroup.value.manufacturer!);
        product.data.manufacturerArticleNumber = trim(dataGroup.value.manufacturerArticleNumber!);
        product.data.supplierArticleNumber = trim(dataGroup.value.supplierArticleNumber!);
        product.orderUnit = dataGroup.value.requestUnit!;
        product.quantity = dataGroup.value.requestQuantity!;
        product.data.numberOfContentUnitsPerOrderUnit = dataGroup.value.numberOfContentUnitsPerOrderUnit!;
        product.data.contentUnit = dataGroup.value.contentUnit!;

        product.price.unitPrice = priceGroup.value.unitPrice!;
        product.price.currency = priceGroup.value.currency!.iso;
        product.price.taxRate = priceGroup.value.taxPercent!;

        product.delivery.deliveryTime = deliveryGroup.value.deliveryTime!;
        product.delivery.deliveryTimeUnit = deliveryGroup.value.deliveryTimeUnit!;
        product.delivery.preferredDeliveryDate = deliveryGroup.value.preferredDeliveryDate?.format('YYYY-MM-DD');
        product.delivery.partialDeliveryPossible = deliveryGroup.value.partialDeliveryPossible!;

        product.comment.comment = trim(comment);
        product.comment.internalComment = trim(commentInternal);
    }
}
